import { type FunctionComponent, type MouseEvent, type MutableRefObject, type PropsWithChildren, type Ref, type RefCallback } from "react";

import classNames from "classnames";

import { Tooltip } from "../../../general/Tooltip";

import CSS from "../styles.module.css";

export interface RTEToggleDefs {
  readonly label: string;
  readonly role?: "button" | "switch";
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly tooltip?: string;
  readonly buttonRef?: Ref<HTMLButtonElement>;
}

export const Toggle: FunctionComponent<PropsWithChildren<RTEToggleDefs>> = ({
  label,
  role = "switch",
  active = false,
  disabled = false,
  onClick,
  tooltip,
  buttonRef,
  children,
}) => {
  const ariaState = role === "switch" ? { "aria-checked": active } : { "aria-pressed": active };
  const content = (ref?: RefCallback<HTMLButtonElement>) => (
    <button
      ref={(el) => {
        ref?.(el);
        if (typeof buttonRef === "function") {
          buttonRef(el);
        } else if (buttonRef) {
          (buttonRef as MutableRefObject<HTMLButtonElement | null>).current = el;
        }
      }}
      type="button"
      className={classNames(CSS.ToolbarButton, { [CSS.active]: active })}
      role={role}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
      {...ariaState}
    >
      {children}
    </button>
  );

  return (
    <>
      {tooltip && (
        <Tooltip content={tooltip} alignment="top">
          {content}
        </Tooltip>
      )}
      {!tooltip && content()}
    </>
  );
};
